/**
 * How a dish, kitchen or person's name reads on screen.
 *
 * Mirrors the server's normalizeDishName (internal/service/menu/dishname.go),
 * which applies to every dish name saved. This covers names saved before that
 * rule existed, kitchen names and people's names, so every card in a list
 * reads the same way however the name was typed: "pepper soup", "PEPPER SOUP"
 * and "pepper SOUP" all become "Pepper Soup".
 *
 * Each word is judged on its own. A word with a capital inside it ("McJollof",
 * "DeLuxe") was cased on purpose and is kept. A short all-capitals word that is
 * a known abbreviation ("bbq", "BBQ") is written in capitals. Everything else becomes title
 * case, with small joining words ("and", "of", "with") left lower case after
 * the first word.
 */
const MINOR_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with'])

const ABBREVIATIONS = new Set(['BBQ', 'XL', 'XXL', 'VIP', 'DIY', 'NG', 'UK', 'USA', 'II', 'III', 'IV'])

const upperFirstLetter = (word: string): string => {
  const chars = Array.from(word)
  const index = chars.findIndex((ch) => ch.toLocaleLowerCase() !== ch.toLocaleUpperCase())
  if (index < 0) return word
  chars[index] = chars[index]!.toLocaleUpperCase()
  return chars.join('')
}

const letters = (word: string): string => Array.from(word).filter((ch) => ch.toLocaleLowerCase() !== ch.toLocaleUpperCase()).join('')

/** A capital after the first letter, alongside lower case: "McJollof", not "EGUSI". */
const isDeliberatelyCased = (part: string): boolean => {
  const body = letters(part).slice(1)
  return body !== body.toLocaleLowerCase() && body !== body.toLocaleUpperCase()
}

const casePart = (part: string): string => {
  if (isDeliberatelyCased(part)) return part
  if (ABBREVIATIONS.has(letters(part).toLocaleUpperCase())) return part.toLocaleUpperCase()
  return upperFirstLetter(part.toLocaleLowerCase())
}

export const displayTitle = (raw: unknown): string => {
  const name = String(raw ?? '').trim().split(/\s+/).filter(Boolean).join(' ')
  if (!name) return ''
  return name
    .split(' ')
    .map((word, index) => {
      const lower = word.toLocaleLowerCase()
      if (index > 0 && MINOR_WORDS.has(lower)) return lower
      return word.split('-').map(casePart).join('-')
    })
    .join(' ')
}
